import { getApiInstance } from './api';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ATPObjective {
  id: string;
  scope: 'week' | 'phase';
  week_start?: string | null;
  phase_key?: string | null;
  title: string;
  criterion: string;
  due_date?: string | null;
  status: 'pending' | 'achieved' | 'not_achieved' | 'insufficient_data';
  result: string;
}
export interface ATPMonitoring {
  target_event_id?: number | null;
  objectives?: ATPObjective[];
  revision?: number;
}
export interface ATPPlan {
  monitoring?: ATPMonitoring | null;
  id: number;
  user_id: number;
  name: string;
  start_week: number;
  end_week: number;
  mode: 'automatic' | 'manual';
  /** Mobile stores model key here: traditional | reverse | polarized | pyramidal */
  training_methodology: string;
  weekly_avg_tss: number;
  weekly_avg_volume: number | null;
  recovery_cycle: 3 | 4;
  current_fitness_level: number | null;
  ctl_start: number;
  /** Backend stores ctl_target in this column */
  ctl_constant: number;
  atl_start: number | null;
  atl_constant: number | null;
  is_customized: 0 | 1;
  needs_recalc: 0 | 1;
  created_at: string;
  updated_at: string;
}

export interface ATPWeekDetails {
  week_number?: number;
  phase_key?: string;
  phase_color?: string;
  tss?: number;
}

export interface ATPWeek {
  id: number;
  atp_id: number;
  user_id: number;
  start_day: string;
  ctl: number | null;
  atl: number | null;
  tsb: number | null;
  period: string;
  atp_type: string | null;
  volume: string | number;
  details: string | ATPWeekDetails | null;
  strength_phase: string | null;
}

export interface AtpPhasePayload {
  key: string;
  label: string;
  color: string;
  weeks: number[];
}

export interface CreateAtpPayload {
  user_id: number;
  name: string;
  start_date: string;
  end_date?: string;
  ctl_start: number;
  ctl_target: number;
  target_event_id?: number | null;
  recovery_cycle?: 3 | 4;
  training_methodology: string;
  weekly_avg_tss: number;
  phases: AtpPhasePayload[];
}

export interface PeriodName {
  id: number;
  name: string;
}

// ─── API module ───────────────────────────────────────────────────────────────
// Matches Mobile\V1\ATPController routes (routes/api/v1/mobile/atp.php).

const BASE = '/api/v1/mobile/atp';

export const atpApi = {
  async getAtp(userId: number): Promise<{ atp: ATPPlan; weeks: ATPWeek[] } | null> {
    const res = await getApiInstance().get(`${BASE}/user/${userId}`);
    const data = res.data.data;
    if (!data?.atp) return null;
    return { atp: data.atp, weeks: data.weeks ?? [] };
  },

  async createAtp(payload: CreateAtpPayload): Promise<{ atp: ATPPlan; weeks: ATPWeek[] }> {
    const res = await getApiInstance().post(BASE, payload);
    return res.data.data;
  },

  async updateAtp(
    atpId: number,
    payload: CreateAtpPayload,
  ): Promise<{ atp: ATPPlan; weeks: ATPWeek[] }> {
    const res = await getApiInstance().put(`${BASE}/${atpId}`, payload);
    return res.data.data;
  },

  async updateWeek(atpId: number, weekId: number, tss: number): Promise<ATPWeek> {
    const res = await getApiInstance().put(`${BASE}/${atpId}/weeks/${weekId}`, { tss });
    return res.data.data.week;
  },

  async updateMonitoring(atpId: number, monitoring: ATPMonitoring): Promise<ATPPlan> {
    const res = await getApiInstance().put(`${BASE}/${atpId}/monitoring`, monitoring);
    return res.data.data.atp;
  },

  async deleteAtp(atpId: number): Promise<void> {
    await getApiInstance().delete(`${BASE}/${atpId}`);
  },

  async getPeriodNames(): Promise<PeriodName[]> {
    const res = await getApiInstance().get(`${BASE}/period-names`);
    return res.data.data?.period_names ?? [];
  },
};
